/**
 * Recording-settings card — admin-configurable per-tier encode profile +
 * global audio toggle. Embedded in the Event tab's right column and in
 * the standalone `/settings` page. Backed by:
 *   - GET  /api/admin/tier-profiles
 *   - PUT  /api/admin/tier-profiles/{tier}
 *   - PUT  /api/admin/capture-config
 *
 * On a successful PUT the backend fans out a `set_capture_settings`
 * fleet command to every phone, so online phones pick up the change
 * within ~1 RTT. Phones that come online later poll the same shape
 * via `/api/fleet/capture-settings` at session start.
 *
 * Auth (Phase 5b): these PUTs are `require_authz`-guarded
 * (`admin.tier_profiles.update`/`admin.capture_config.update` —
 * superadmin only) via the same-origin session cookie + CSRF header
 * `services/api.ts`'s axios client attaches automatically — no bearer
 * to read or pass here anymore.
 *
 * `when_to_use` is an operator note: stored and shown back here, never
 * interpreted by the phone.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Archive, Mic, Save, UploadCloud, Video, Wifi } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import api from '@/services/api';
import type { CaptureSettingsBundle, DefaultUploadPolicy, TierProfile } from '@/types';

const RESOLUTIONS: Array<{ value: string; width: number; height: number; label: string }> = [
  { value: '1920x1080', width: 1920, height: 1080, label: '1080p (1920×1080)' },
  { value: '1280x720', width: 1280, height: 720, label: '720p (1280×720)' },
  { value: '854x480', width: 854, height: 480, label: '480p (854×480)' },
  { value: '640x360', width: 640, height: 360, label: '360p (640×360)' },
];

const FPS_OPTIONS = [24, 25, 30, 60];

const TIER_BITRATE_CAP_KBPS: Record<'stream' | 'archive', number> = {
  stream: 5_000,
  archive: 50_000,
};

const BITRATE_MIN_KBPS = 200;

function formatTimestamp(value: string | null): string {
  if (!value) return 'never';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

interface TierRowProps {
  profile: TierProfile;
  onSave: (next: TierProfile) => Promise<void>;
}

const TierRow: React.FC<TierRowProps> = ({ profile, onSave }) => {
  const [width, setWidth] = useState(profile.width);
  const [height, setHeight] = useState(profile.height);
  const [bitrateKbps, setBitrateKbps] = useState(profile.bitrate_bps / 1000);
  const [fps, setFps] = useState(profile.fps);
  const [whenToUse, setWhenToUse] = useState(profile.when_to_use ?? '');
  const [saving, setSaving] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  useEffect(() => {
    setWidth(profile.width);
    setHeight(profile.height);
    setBitrateKbps(profile.bitrate_bps / 1000);
    setFps(profile.fps);
    setWhenToUse(profile.when_to_use ?? '');
  }, [profile]);

  const resolutionValue = `${width}x${height}`;
  const tierLabel = profile.tier === 'archive' ? 'High quality' : 'Low quality';
  const Icon = profile.tier === 'archive' ? Archive : Video;
  const bitrateMax = TIER_BITRATE_CAP_KBPS[profile.tier];

  const dirty =
    width !== profile.width ||
    height !== profile.height ||
    Math.round(bitrateKbps) !== Math.round(profile.bitrate_bps / 1000) ||
    fps !== profile.fps ||
    (whenToUse || '') !== (profile.when_to_use ?? '');

  const handleSave = async () => {
    setSaving(true);
    setErrorText(null);
    try {
      await onSave({
        ...profile,
        width,
        height,
        bitrate_bps: Math.round(bitrateKbps) * 1000,
        fps,
        when_to_use: whenToUse.trim() ? whenToUse.trim() : null,
      });
    } catch (e) {
      const msg =
        (e as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail ??
        (e as { message?: string })?.message ??
        'save failed';
      setErrorText(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-md border border-border bg-bg-elevated/40 p-3">
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-4 w-4" />
        <h3 className="text-sm font-semibold">{tierLabel}</h3>
        <span className="ml-auto text-[10px] text-fg-secondary">rev {profile.revision}</span>
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-fg-secondary">
              Resolution
            </label>
            <Select
              value={resolutionValue}
              onValueChange={(v) => {
                const r = RESOLUTIONS.find((row) => row.value === v);
                if (r) {
                  setWidth(r.width);
                  setHeight(r.height);
                }
              }}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RESOLUTIONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-fg-secondary">
              Frame rate
            </label>
            <Select value={String(fps)} onValueChange={(v) => setFps(Number(v))}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FPS_OPTIONS.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n} fps
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="text-[10px] font-medium uppercase tracking-wide text-fg-secondary">
              Bitrate
            </label>
            <span className="font-mono text-xs">{(bitrateKbps / 1000).toFixed(2)} Mbps</span>
          </div>
          <Slider
            min={BITRATE_MIN_KBPS}
            max={bitrateMax}
            step={100}
            value={[bitrateKbps]}
            onValueChange={(values) => setBitrateKbps(values[0] ?? bitrateKbps)}
          />
          <div className="mt-1 flex justify-between text-[10px] text-fg-secondary">
            <span>{(BITRATE_MIN_KBPS / 1000).toFixed(1)} Mbps</span>
            <span>{(bitrateMax / 1000).toFixed(0)} Mbps</span>
          </div>
        </div>
        <div>
          <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-fg-secondary">
            When to use
          </label>
          <textarea
            value={whenToUse}
            onChange={(e) => setWhenToUse(e.target.value)}
            rows={2}
            placeholder="e.g. 'long gravel stages', 'high-stakes events'"
            className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            maxLength={2000}
          />
        </div>
      </div>
      {errorText && <p className="mt-2 text-xs text-destructive">{errorText}</p>}
      <div className="mt-3 flex items-center justify-between gap-2">
        <p className="text-[10px] text-fg-secondary">
          Updated {formatTimestamp(profile.updated_at)}
        </p>
        <Button size="sm" onClick={handleSave} disabled={!dirty || saving} className="h-7 px-2 text-xs">
          <Save className="mr-1 h-3 w-3" /> Save
        </Button>
      </div>
    </div>
  );
};

interface DefaultUploadPolicyRowProps {
  initial: DefaultUploadPolicy;
  onSave: (next: DefaultUploadPolicy) => Promise<void>;
}

const DefaultUploadPolicyRow: React.FC<DefaultUploadPolicyRowProps> = ({ initial, onSave }) => {
  const [policy, setPolicy] = useState<DefaultUploadPolicy>(initial);
  const [saving, setSaving] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  useEffect(() => {
    setPolicy(initial);
  }, [initial]);

  const dirty =
    policy.upload_policy !== initial.upload_policy ||
    policy.adaptive_enter_low_s !== initial.adaptive_enter_low_s ||
    policy.adaptive_recover_high_s !== initial.adaptive_recover_high_s ||
    policy.wifi_only_archive !== initial.wifi_only_archive;

  const handleSave = async () => {
    setSaving(true);
    setErrorText(null);
    try {
      await onSave(policy);
    } catch (e) {
      const msg =
        (e as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail ??
        (e as { message?: string })?.message ??
        'save failed';
      setErrorText(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-md border border-border bg-bg-elevated/40 p-3 space-y-2">
      <div className="flex items-center gap-2">
        <UploadCloud className="h-4 w-4" />
        <h3 className="text-sm font-semibold">Default upload policy</h3>
      </div>
      <p className="text-[10px] text-fg-secondary">
        Used by events that don't override their per-event policy.
      </p>
      <Select
        value={policy.upload_policy}
        onValueChange={(v) =>
          setPolicy({ ...policy, upload_policy: v as DefaultUploadPolicy['upload_policy'] })
        }
      >
        <SelectTrigger className="h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="high_only">High quality only (live; no low-quality preview — recommended)</SelectItem>
          <SelectItem value="deferred_high">Low quality live, High quality after</SelectItem>
        </SelectContent>
      </Select>
      {policy.upload_policy === 'deferred_high' && (
        <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-bg-elevated/40 p-2">
          <div className="flex items-center gap-2 text-xs">
            <Wifi className="h-3 w-3" /> Archive only on wifi
          </div>
          <Switch
            checked={policy.wifi_only_archive}
            onCheckedChange={(v) => setPolicy({ ...policy, wifi_only_archive: v })}
          />
        </div>
      )}
      {errorText && <p className="text-xs text-destructive">{errorText}</p>}
      <div className="flex items-center justify-end">
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!dirty || saving}
          className="h-7 px-2 text-xs"
        >
          <Save className="mr-1 h-3 w-3" /> Save
        </Button>
      </div>
    </div>
  );
};

export const RecordingSettingsCard: React.FC = () => {
  const [bundle, setBundle] = useState<CaptureSettingsBundle | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [audioSaving, setAudioSaving] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getCaptureSettings();
        if (!cancelled) setBundle(data);
      } catch (e) {
        if (!cancelled) {
          const msg =
            (e as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail ??
            (e as { message?: string })?.message ??
            'failed to load';
          setLoadError(msg);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const tiers = useMemo(() => {
    if (!bundle) return null;
    const archive = bundle.tiers.find((t) => t.tier === 'archive');
    const stream = bundle.tiers.find((t) => t.tier === 'stream');
    return { archive, stream };
  }, [bundle]);

  const handleSaveTier = async (next: TierProfile) => {
    const updated = await api.updateTierProfile(next.tier, {
      width: next.width,
      height: next.height,
      bitrate_bps: next.bitrate_bps,
      fps: next.fps,
      when_to_use: next.when_to_use,
    });
    setBundle(updated);
  };

  const [stabSaving, setStabSaving] = useState(false);
  const [stabError, setStabError] = useState<string | null>(null);
  const handleStabilizationDefault = async (value: string) => {
    if (!bundle) return;
    setStabSaving(true);
    setStabError(null);
    try {
      const updated = await api.updateStabilizationDefault(
        value === 'auto' ? null : (value as 'off' | 'eis' | 'ois' | 'both'),
      );
      setBundle(updated);
    } catch (e) {
      const msg =
        (e as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail ??
        (e as { message?: string })?.message ??
        'save failed';
      setStabError(msg);
    } finally {
      setStabSaving(false);
    }
  };

  const handleAudioToggle = async (audioEnabled: boolean) => {
    if (!bundle) return;
    setAudioSaving(true);
    setAudioError(null);
    try {
      const updated = await api.updateAudioConfig(audioEnabled);
      setBundle(updated);
    } catch (e) {
      const msg =
        (e as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail ??
        (e as { message?: string })?.message ??
        'save failed';
      setAudioError(msg);
    } finally {
      setAudioSaving(false);
    }
  };

  const handleDefaultPolicySave = async (next: DefaultUploadPolicy) => {
    const updated = await api.updateDefaultUploadPolicy(next);
    setBundle(updated);
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-3 flex items-center gap-2">
          <Video className="h-4 w-4 text-accent" />
          <h2 className="text-base font-semibold">Recording settings</h2>
        </div>
        {loadError && (
          <p className="mb-2 text-xs text-destructive">Failed to load: {loadError}</p>
        )}
        {bundle && (
          <div className="space-y-3">
            <div className="flex items-start gap-3 rounded-md border border-border bg-bg-elevated/40 p-3">
              <Mic className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Capture audio</span>
                  <Switch
                    checked={bundle.audio.audio_enabled}
                    disabled={audioSaving}
                    onCheckedChange={handleAudioToggle}
                  />
                </div>
                <p className="mt-1 text-[10px] text-fg-secondary">
                  AAC-LC 48 kHz mono / 128 kbps. Bitrate, sample rate, and channels are fixed in
                  this build.
                </p>
                {audioError && (
                  <p className="mt-1 text-xs text-destructive">{audioError}</p>
                )}
                <p className="mt-1 text-[10px] text-fg-secondary">
                  rev {bundle.audio.revision} — updated{' '}
                  {formatTimestamp(bundle.audio.updated_at)}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-md border border-border bg-bg-elevated/40 p-3">
              <Video className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="flex-1">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium">Video stabilization (fleet default)</span>
                  <Select
                    value={bundle.audio.stabilization_default ?? 'auto'}
                    disabled={stabSaving}
                    onValueChange={(v) => void handleStabilizationDefault(v)}
                  >
                    <SelectTrigger className="w-[190px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto (prefer EIS)</SelectItem>
                      <SelectItem value="off">Off</SelectItem>
                      <SelectItem value="eis">EIS (electronic)</SelectItem>
                      <SelectItem value="ois">OIS (optical)</SelectItem>
                      <SelectItem value="both">EIS + OIS</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="mt-1 text-[10px] text-fg-secondary">
                  Pushed to every phone without a per-device override (Device page → Camera card).
                  Phones validate against their camera and fall back safely; applies on each
                  phone&apos;s next recording session. OIS can&apos;t warp the image — the first
                  thing to try against rolling-shutter jello.
                </p>
                {stabError && <p className="mt-1 text-xs text-destructive">{stabError}</p>}
              </div>
            </div>
            {tiers?.stream && <TierRow profile={tiers.stream} onSave={handleSaveTier} />}
            {tiers?.archive && <TierRow profile={tiers.archive} onSave={handleSaveTier} />}
            <DefaultUploadPolicyRow
              initial={bundle.default_upload_policy}
              onSave={handleDefaultPolicySave}
            />
            <p className="text-[10px] text-fg-secondary">
              Tier + audio changes apply to the next session each phone records. Default upload
              policy applies only to events that haven't overridden it; the per-event policy lives
              on the Event tab.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RecordingSettingsCard;
